import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Cancel as CancelIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Flag as FlagIcon,
  History as HistoryIcon,
  Login as LoginIcon,
  LockReset as LockResetIcon,
  MoreTime as MoreTimeIcon,
  MoreVert as MoreVertIcon,
  Palette as PaletteIcon,
  Pause as PauseIcon,
  Payments as MoneyIcon,
  Phone as PhoneIcon,
  PlayArrow as PlayArrowIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  Settings as SettingsIcon,
  SwapHoriz as SwapHorizIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { superAdminApi, SaPlan, SaTenantRow } from '../../services/superAdminApi';

// ==================== Helpers ====================

const STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'primary' | 'default' }> = {
  Active: { label: 'Activa', color: 'success' },
  Demo: { label: 'Demo', color: 'info' },
  Free: { label: 'Gratis', color: 'default' },
  Suspended: { label: 'Suspendida', color: 'warning' },
  Cancelled: { label: 'Cancelada', color: 'error' },
  Expired: { label: 'Expirada', color: 'error' },
  PendingPayment: { label: 'Pago pendiente', color: 'primary' },
};

const MONTH_OPTIONS = [1, 3, 6, 12];

const DEFAULT_TEMPLATE = `Hola {adminName}!

Te comparto los datos de acceso a {tenantName} en DashFlow:

URL: {loginUrl}
Usuario: {adminEmail}

Si tenés alguna duda, escribinos!`;
const TEMPLATE_KEY = 'dashflow_sa_access_message_template';

const errMsg = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

/** Día de hoy en Argentina como "yyyy-mm-dd". */
const todayAr = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date());
const dayAr = (iso: string) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date(iso));

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' }) : '—';

/** Días entre hoy y la fecha, por día calendario de Argentina (negativo = ya pasó). */
const daysUntil = (iso?: string | null) => {
  if (!iso) return null;
  const a = new Date(`${todayAr()}T00:00:00Z`).getTime();
  const b = new Date(`${dayAr(iso)}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86400000);
};

const money = (n?: number | null, currency?: string | null) =>
  `${currency === 'USD' ? 'US$' : '$'}${Math.round(Number(n || 0)).toLocaleString('es-AR')}`;

const waDigits = (phone?: string | null) => {
  let d = (phone || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('00')) d = d.slice(2);
  if (d.startsWith('0')) d = d.slice(1);
  if (!d.startsWith('54')) d = `549${d}`;
  return d;
};

const copy = async (text: string) => {
  await navigator.clipboard.writeText(text);
};

type Toast = { type: 'success' | 'error' | 'info'; text: string } | null;

type DialogKind =
  | 'changePlan' | 'billingCountry' | 'manualPayment' | 'adminWhatsApp' | 'editPhone' | 'accessMessage' | 'template'
  | 'colors' | 'flags' | 'resetPassword' | 'setPassword' | 'history' | 'suspend' | 'cancel' | 'extendDemo' | 'delete'
  | 'billingEmail';

// ==================== Página ====================

/**
 * Gestión de tenants del super-admin (port de la pestaña Tenants de GymHero): vencimientos, uso,
 * cobros, pago manual, plan, demo, suspensión/cancelación con motivo, contraseñas y mensajes al admin.
 */
export const SATenants: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SaTenantRow[]>([]);
  const [plans, setPlans] = useState<SaPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<Toast>(null);

  const [search, setSearch] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [hideExcluded, setHideExcluded] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [sel, setSel] = useState<SaTenantRow | null>(null);
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [busy, setBusy] = useState(false);

  const ok = (text: string) => setToast({ type: 'success', text });
  const fail = (e: any, fallback: string) => setToast({ type: 'error', text: errMsg(e, fallback) });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([superAdminApi.getTenantsOverview(), superAdminApi.getPlansForTenants()]);
      setRows(r);
      setPlans(p);
    } catch (e) {
      fail(e, 'No se pudieron cargar los tenants');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const phone = phoneSearch.replace(/\D/g, '');
    return rows.filter(t => {
      if (hideExcluded && t.excludeFromMetrics) return false;
      if (q && ![t.name, t.subdomain, t.adminEmail, t.adminUserEmail].some(x => (x || '').toLowerCase().includes(q))) return false;
      if (phone && !(t.adminPhone || '').replace(/\D/g, '').includes(phone)) return false;
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (planFilter !== 'all' && String(t.planId ?? '') !== planFilter) return false;
      if (expiryFilter !== 'all') {
        const d = daysUntil(t.accessEndsAt);
        if (expiryFilter === 'expiring' && !(d !== null && d >= 0 && d <= 30)) return false;
        if (expiryFilter === 'expired' && !(d !== null && d < 0)) return false;
        if (expiryFilter === 'no-expiry' && d !== null) return false;
      }
      return true;
    });
  }, [rows, search, phoneSearch, statusFilter, planFilter, expiryFilter, hideExcluded]);

  const counted = rows.filter(t => !t.excludeFromMetrics);
  const thisMonth = todayAr().slice(0, 7);
  const stats = [
    { label: 'Activos (pagos)', value: counted.filter(t => t.status === 'Active' && t.subscriptionActive).length },
    { label: 'Próximos a vencer (≤30 días)', value: counted.filter(t => { const d = daysUntil(t.accessEndsAt); return d !== null && d >= 0 && d <= 30; }).length },
    { label: 'Vencidos', value: counted.filter(t => { const d = daysUntil(t.accessEndsAt); return d !== null && d < 0 && t.status !== 'Free'; }).length },
    { label: 'Con pago este mes', value: counted.filter(t => t.lastPaymentAt && dayAr(t.lastPaymentAt).slice(0, 7) === thisMonth).length },
  ];

  const openMenu = (e: React.MouseEvent<HTMLElement>, t: SaTenantRow) => { setMenuAnchor(e.currentTarget); setSel(t); };
  const open = (kind: DialogKind) => { setMenuAnchor(null); setDialog(kind); };
  const close = () => setDialog(null);

  const impersonate = async (t: SaTenantRow) => {
    try {
      const resp = await superAdminApi.impersonate(t.id);
      if (!resp.success || !resp.data) throw new Error(resp.message || 'No se pudo ingresar');
      localStorage.setItem('auth_token', resp.data.token);
      localStorage.setItem('auth_user', JSON.stringify(resp.data.user));
      localStorage.setItem('auth_tenant', JSON.stringify(resp.data.tenant));
      window.location.href = '/dashboard';
    } catch (e) {
      fail(e, 'No se pudo ingresar como admin');
    }
  };

  /** Acción simple con mensaje y recarga. */
  const run = async (fn: () => Promise<any>, fallback: string) => {
    setBusy(true);
    try {
      const r = await fn();
      ok(r?.message || 'Listo');
      close();
      await load();
      return true;
    } catch (e) {
      fail(e, fallback);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const sendWhatsApp = async (t: SaTenantRow) => {
    setMenuAnchor(null);
    try {
      const { message, phoneNumber } = await superAdminApi.getWhatsAppReminder(t.id);
      if (!phoneNumber) { setToast({ type: 'error', text: 'El teléfono del admin no está cargado' }); return; }
      window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    } catch (e) {
      fail(e, 'No se pudo armar el recordatorio');
    }
  };

  const statusChip = (t: SaTenantRow) => {
    const s = STATUS[t.status] || { label: t.status, color: 'default' as const };
    const chip = <Chip size="small" label={s.label} color={s.color} />;
    return t.statusReason ? <Tooltip title={`Motivo: ${t.statusReason}`}>{chip}</Tooltip> : chip;
  };

  const expiryCell = (t: SaTenantRow) => {
    const d = daysUntil(t.accessEndsAt);
    return (
      <Box>
        <Typography variant="body2" color={d !== null && d < 0 ? 'error.main' : d !== null && d <= 30 ? 'warning.main' : undefined}>
          {t.accessEndsAt ? fmtDate(t.accessEndsAt) : 'Sin fecha'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">{t.status === 'Demo' ? 'Fin de la demo' : 'Cobertura actual'}</Typography>
        {d !== null && t.status !== 'Free' && (
          d > 0 && d <= 30 ? <Chip size="small" color="warning" label={`Vence en ${d} días`} sx={{ mt: 0.5 }} />
            : d === 0 ? <Chip size="small" color="warning" label="Vence hoy" sx={{ mt: 0.5 }} />
              : d < 0 ? <Chip size="small" color="error" label={`Vencido hace ${-d} días`} sx={{ mt: 0.5 }} /> : null
        )}
      </Box>
    );
  };

  const s = sel;
  const status = s?.status || '';

  return (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700}>Gestión de Tenants</Typography>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={load} disabled={loading}>Actualizar</Button>
            <Button variant="contained" onClick={() => navigate('/super-admin/tenants/new')}>Crear tenant</Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {stats.map(x => (
            <Grid item xs={6} md={3} key={x.label}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{x.label}</Typography>
                <Typography variant="h6" fontWeight={700}>{x.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Buscar por nombre, subdominio o email" value={search} onChange={e => setSearch(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" label="Buscar por teléfono" value={phoneSearch} onChange={e => setPhoneSearch(e.target.value)} placeholder="Ej: 1155" />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Estado" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <MenuItem value="all">Todos</MenuItem>
              {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Plan" value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
              <MenuItem value="all">Todos</MenuItem>
              {plans.map(p => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Vencimiento" value={expiryFilter} onChange={e => setExpiryFilter(e.target.value)}>
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="expiring">Próx. a vencer</MenuItem>
              <MenuItem value="expired">Vencidos</MenuItem>
              <MenuItem value="no-expiry">Sin fecha</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={1}>
            <FormControlLabel control={<Checkbox size="small" checked={hideExcluded} onChange={e => setHideExcluded(e.target.checked)} />}
              label={<Typography variant="caption">Ocultar pruebas</Typography>} />
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Negocio</TableCell>
                  <TableCell>Alta</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Precio</TableCell>
                  <TableCell>Extras</TableCell>
                  <TableCell>Uso</TableCell>
                  <TableCell>Ventas del negocio</TableCell>
                  <TableCell>Último pago</TableCell>
                  <TableCell>Expira</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(t => (
                  <TableRow key={t.id} hover sx={{ opacity: t.excludeFromMetrics ? 0.65 : 1 }}>
                    <TableCell>
                      <Typography variant="subtitle2">{t.name} <Typography component="span" variant="caption" color="text.secondary">#{t.id}</Typography></Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{t.subdomain}</Typography>
                      {(t.adminUserEmail || t.adminEmail) && <Typography variant="caption" color="text.secondary" display="block">{t.adminUserEmail || t.adminEmail}</Typography>}
                      {t.adminPhone && <Typography variant="caption" color="text.secondary" display="block">{t.adminPhone}</Typography>}
                      {t.excludeFromMetrics && <Chip size="small" variant="outlined" label="Prueba interna" sx={{ mt: 0.5 }} />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fmtDate(t.createdAt)}</Typography>
                      {t.businessKind && <Typography variant="caption" color="text.secondary">{t.businessKind}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="body2">{t.plan || '—'}</Typography></TableCell>
                    <TableCell>{statusChip(t)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {t.status === 'Demo' ? 'Demo gratis' : t.status === 'Free' || !t.planPrice ? 'Gratis' : `${money(t.planPrice, t.planCurrency)}/mes`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        {t.paidVia && <Chip size="small" label={`Paga: ${t.paidVia}`} color="success" variant="outlined" />}
                        {t.invoicingTier && t.invoicingTier !== 'none' && <Chip size="small" label={`ARCA ${t.invoicingTier}`} />}
                        {t.mercadoPagoConnected && <Chip size="small" label="MP conectado" />}
                        {t.enabledFlags.length > 0 && (
                          <Tooltip title={t.enabledFlags.join(', ')}><Chip size="small" variant="outlined" label={`${t.enabledFlags.length} flags`} /></Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.usersCount} {t.usersCount === 1 ? 'usuario' : 'usuarios'}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{t.productsCount} productos · {t.orders30} ventas 30d</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">Último ingreso: {fmtDate(t.lastLoginAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="success.main">{money(t.revenue30)} último mes</Typography>
                      <Typography variant="caption" color="text.secondary">{money(t.revenue365)} último año</Typography>
                    </TableCell>
                    <TableCell>
                      {t.lastPaymentAt ? (
                        <>
                          <Typography variant="body2">{fmtDate(t.lastPaymentAt)}</Typography>
                          <Typography variant="caption" color="text.secondary">{money(t.lastPaymentAmount, t.lastPaymentCurrency)} · {t.paymentsCount} {t.paymentsCount === 1 ? 'pago' : 'pagos'}</Typography>
                        </>
                      ) : <Typography variant="caption" color="text.secondary">Nunca</Typography>}
                    </TableCell>
                    <TableCell>{expiryCell(t)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Ingresar como admin del negocio"><IconButton size="small" color="primary" onClick={() => impersonate(t)}><LoginIcon /></IconButton></Tooltip>
                      <IconButton size="small" onClick={e => openMenu(e, t)} aria-label="Más acciones"><MoreVertIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={11} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>No hay tenants con esos filtros</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ==================== Menú de acciones ==================== */}
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => open('changePlan')}><ListItemIcon><SwapHorizIcon /></ListItemIcon><ListItemText>Cambiar plan</ListItemText></MenuItem>
          <MenuItem onClick={() => open('billingCountry')}><ListItemIcon><PublicIcon /></ListItemIcon><ListItemText>País y medio de cobro</ListItemText></MenuItem>
          <MenuItem onClick={() => open('manualPayment')}><ListItemIcon><MoneyIcon /></ListItemIcon><ListItemText>Registrar pago manual</ListItemText></MenuItem>
          <MenuItem onClick={() => s && sendWhatsApp(s)}><ListItemIcon sx={{ color: 'success.main' }}><WhatsAppIcon /></ListItemIcon><ListItemText>Enviar recordatorio por WhatsApp</ListItemText></MenuItem>
          <MenuItem onClick={() => open('adminWhatsApp')}><ListItemIcon sx={{ color: 'success.main' }}><WhatsAppIcon /></ListItemIcon><ListItemText>Copiar WhatsApp del admin</ListItemText></MenuItem>
          <MenuItem onClick={() => open('editPhone')}><ListItemIcon><PhoneIcon /></ListItemIcon><ListItemText>Editar teléfono</ListItemText></MenuItem>
          <MenuItem onClick={() => open('accessMessage')}><ListItemIcon><ContentCopyIcon /></ListItemIcon><ListItemText>Copiar mensaje de acceso</ListItemText></MenuItem>
          <MenuItem onClick={() => open('colors')}><ListItemIcon><PaletteIcon /></ListItemIcon><ListItemText>Configurar colores</ListItemText></MenuItem>
          <MenuItem onClick={() => open('flags')}><ListItemIcon><FlagIcon /></ListItemIcon><ListItemText>Feature flags</ListItemText></MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); s && run(() => superAdminApi.setExcludeFromMetrics(s.id, !s.excludeFromMetrics), 'No se pudo cambiar'); }}>
            <ListItemIcon><BarChartIcon /></ListItemIcon>
            <ListItemText>{s?.excludeFromMetrics ? 'Volver a contar en métricas' : 'Excluir de métricas (cuenta de prueba)'}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => open('resetPassword')}><ListItemIcon><LockResetIcon /></ListItemIcon><ListItemText>Mandar mail para restablecer contraseña</ListItemText></MenuItem>
          <MenuItem onClick={() => open('setPassword')}><ListItemIcon><LockResetIcon /></ListItemIcon><ListItemText>Establecer contraseña (directo)</ListItemText></MenuItem>
          <MenuItem onClick={() => open('history')}><ListItemIcon><HistoryIcon /></ListItemIcon><ListItemText>Historial de acciones</ListItemText></MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); s && navigate(`/super-admin/tenants/${s.id}`); }}><ListItemIcon><EditIcon /></ListItemIcon><ListItemText>Editar todos los datos</ListItemText></MenuItem>
          <Divider />
          <MenuItem disabled={!['Active', 'Demo', 'Free', 'PendingPayment'].includes(status)} onClick={() => open('suspend')}>
            <ListItemIcon><PauseIcon /></ListItemIcon><ListItemText>Suspender</ListItemText>
          </MenuItem>
          <MenuItem disabled={status !== 'Suspended'} onClick={() => { setMenuAnchor(null); s && run(() => superAdminApi.lifecycle(s.id, 'activate'), 'No se pudo activar'); }}>
            <ListItemIcon><PlayArrowIcon /></ListItemIcon><ListItemText>Reactivar</ListItemText>
          </MenuItem>
          {['Demo', 'Free', 'PendingPayment'].includes(status) && (
            <MenuItem sx={{ color: 'success.main' }} onClick={() => { setMenuAnchor(null); s && run(() => superAdminApi.lifecycle(s.id, 'activate'), 'No se pudo activar'); }}>
              <ListItemIcon sx={{ color: 'success.main' }}><MoneyIcon /></ListItemIcon><ListItemText>Activar suscripción paga (1 mes)</ListItemText>
            </MenuItem>
          )}
          <MenuItem disabled={!['Demo', 'Free', 'Expired', 'Cancelled'].includes(status)} onClick={() => open('extendDemo')}>
            <ListItemIcon><MoreTimeIcon /></ListItemIcon><ListItemText>Extender demo</ListItemText>
          </MenuItem>
          <MenuItem disabled={status !== 'Expired' && status !== 'Cancelled'} onClick={() => { setMenuAnchor(null); s && run(() => superAdminApi.lifecycle(s.id, 'renew'), 'No se pudo renovar'); }}>
            <ListItemIcon><RestoreIcon /></ListItemIcon><ListItemText>Renovar (1 mes)</ListItemText>
          </MenuItem>
          <MenuItem disabled={status === 'Cancelled'} onClick={() => open('cancel')}>
            <ListItemIcon><CancelIcon /></ListItemIcon><ListItemText>Cancelar suscripción</ListItemText>
          </MenuItem>
          <MenuItem sx={{ color: 'error.main' }} onClick={() => open('delete')}>
            <ListItemIcon sx={{ color: 'error.main' }}><DeleteIcon /></ListItemIcon><ListItemText>Eliminar tenant</ListItemText>
          </MenuItem>
        </Menu>

        {s && (
          <>
            <ChangePlanDialog open={dialog === 'changePlan'} tenant={s} plans={plans} busy={busy} onClose={close}
              onEditBillingEmail={() => setDialog('billingEmail')}
              onSave={p => run(() => superAdminApi.changePlan(s.id, p), 'No se pudo cambiar el plan')} />
            <BillingEmailDialog open={dialog === 'billingEmail'} tenant={s} busy={busy} onClose={() => setDialog('changePlan')}
              onSave={email => run(() => superAdminApi.updateTenantProfile(s.id, { billingEmail: email }), 'No se pudo guardar')} />
            <BillingCountryDialog open={dialog === 'billingCountry'} tenant={s} busy={busy} onClose={close}
              onSave={p => run(() => superAdminApi.updateTenantProfile(s.id, p), 'No se pudo guardar')} />
            <ManualPaymentDialog open={dialog === 'manualPayment'} tenant={s} plans={plans} busy={busy} onClose={close}
              onSave={p => run(() => superAdminApi.manualPayment(s.id, p), 'No se pudo registrar el pago')}
              onCopyReminder={async () => {
                try {
                  const r = await superAdminApi.getWhatsAppReminder(s.id);
                  await copy(r.message);
                  setToast({ type: 'info', text: 'Mensaje de recordatorio copiado' });
                  return r.message;
                } catch (e) { fail(e, 'No se pudo armar el mensaje'); return ''; }
              }} />
            <AdminWhatsAppDialog open={dialog === 'adminWhatsApp'} tenant={s} onClose={close} onCopied={t => setToast({ type: 'success', text: t })} />
            <EditPhoneDialog open={dialog === 'editPhone'} tenant={s} busy={busy} onClose={close}
              onSave={phone => run(() => superAdminApi.updateTenantProfile(s.id, { adminPhone: phone }), 'No se pudo guardar el teléfono')} />
            <AccessMessageDialog open={dialog === 'accessMessage'} tenant={s} onClose={close} onEditTemplate={() => setDialog('template')}
              onCopied={() => ok('Mensaje copiado al portapapeles')} onError={e => fail(e, 'No se pudo armar el mensaje')} />
            <TemplateDialog open={dialog === 'template'} onClose={() => setDialog('accessMessage')} />
            <ColorsDialog open={dialog === 'colors'} tenant={s} busy={busy} onClose={close}
              onSave={p => run(() => superAdminApi.updateTenantProfile(s.id, p), 'No se pudieron guardar los colores')} />
            <FlagsDialog open={dialog === 'flags'} tenant={s} onClose={close} onSaved={() => { ok('Feature flags guardados'); close(); load(); }} onError={e => fail(e, 'No se pudieron guardar los flags')} />
            <ResetPasswordDialog open={dialog === 'resetPassword'} tenant={s} busy={busy} onClose={close}
              onSave={email => run(() => superAdminApi.sendPasswordReset(email), 'No se pudo mandar el mail')} />
            <SetPasswordDialog open={dialog === 'setPassword'} tenant={s} busy={busy} onClose={close}
              onSave={p => run(() => superAdminApi.setPasswordByEmail(p), 'No se pudo cambiar la contraseña')} />
            <HistoryDialog open={dialog === 'history'} tenant={s} onClose={close} />
            <ReasonDialog open={dialog === 'suspend'} title="Suspender" tenant={s} busy={busy} color="warning"
              text="El negocio no va a poder usar el sistema hasta que lo reactives." onClose={close}
              onSave={reason => run(() => superAdminApi.lifecycle(s.id, 'suspend', reason), 'No se pudo suspender')} />
            <ReasonDialog open={dialog === 'cancel'} title="Cancelar suscripción" tenant={s} busy={busy} color="error"
              text="La suscripción queda cancelada y el negocio pierde el acceso. Se puede renovar después." onClose={close}
              onSave={reason => run(() => superAdminApi.lifecycle(s.id, 'cancel', reason), 'No se pudo cancelar')} />
            <ExtendDemoDialog open={dialog === 'extendDemo'} tenant={s} busy={busy} onClose={close}
              onSave={days => run(() => superAdminApi.extendDemo(s.id, days), 'No se pudo extender la demo')} />
            <DeleteDialog open={dialog === 'delete'} tenant={s} busy={busy} onClose={close}
              onSave={() => run(() => superAdminApi.deleteTenant(s.id), 'No se pudo eliminar')} />
          </>
        )}

        <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          {toast ? <Alert severity={toast.type} onClose={() => setToast(null)} variant="filled">{toast.text}</Alert> : <span />}
        </Snackbar>
      </CardContent>
    </Card>
  );
};

// ==================== Diálogos ====================

type Base = { open: boolean; tenant: SaTenantRow; onClose: () => void };
type WithSave<T> = Base & { busy: boolean; onSave: (v: T) => Promise<any> };

const Actions: React.FC<{ onClose: () => void; onSave: () => void; busy: boolean; label?: string; color?: 'primary' | 'error' | 'warning' | 'success'; disabled?: boolean }> =
  ({ onClose, onSave, busy, label = 'Guardar', color = 'primary', disabled }) => (
    <DialogActions>
      <Button onClick={onClose} disabled={busy}>Cancelar</Button>
      <Button variant="contained" color={color} onClick={onSave} disabled={busy || disabled}>{busy ? <CircularProgress size={20} /> : label}</Button>
    </DialogActions>
  );

const ChangePlanDialog: React.FC<WithSave<{ planId: number; months: number; effectiveDate?: string; reason?: string }> & { plans: SaPlan[]; onEditBillingEmail: () => void }> =
  ({ open, tenant, plans, busy, onClose, onSave, onEditBillingEmail }) => {
    const [planId, setPlanId] = useState<number | ''>('');
    const [months, setMonths] = useState(1);
    const [effective, setEffective] = useState('');
    const [reason, setReason] = useState('');
    useEffect(() => { if (open) { setPlanId(tenant.planId ?? ''); setMonths(1); setEffective(''); setReason(''); } }, [open, tenant]);
    const plan = plans.find(p => p.id === planId);
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Cambiar plan · {tenant.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              <Typography variant="body2"><b>Email admin:</b> {tenant.adminEmail || '—'}</Typography>
              <Typography variant="body2"><b>Email de MercadoPago:</b> {tenant.billingEmail || '(usa el del admin)'}</Typography>
              <Button size="small" startIcon={<EditIcon />} onClick={onEditBillingEmail} sx={{ mt: 0.5 }}>Editar email de MercadoPago</Button>
            </Alert>
            <TextField select label="Nuevo plan" value={planId} onChange={e => setPlanId(Number(e.target.value))}>
              {plans.filter(p => p.isActive || p.id === tenant.planId).map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name} — {p.price > 0 ? `${money(p.price, p.currency)}/mes` : 'Gratis'}</MenuItem>
              ))}
            </TextField>
            {plan && plan.code !== 'free' && (
              <>
                <TextField select label="Cantidad de meses del período" value={months} onChange={e => setMonths(Number(e.target.value))}
                  helperText={tenant.status === 'Active'
                    ? `Fija la cobertura en ${months} ${months === 1 ? 'mes' : 'meses'} desde la fecha efectiva. Total ${money(plan.price * months, plan.currency)}`
                    : 'Solo cambia la cobertura si el tenant ya es pago; para cobrar usá "Registrar pago manual".'}>
                  {MONTH_OPTIONS.map(m => <MenuItem key={m} value={m}>{m} {m === 1 ? 'mes' : 'meses'}</MenuItem>)}
                </TextField>
                <TextField type="date" label="Fecha efectiva (opcional)" value={effective} onChange={e => setEffective(e.target.value)}
                  InputLabelProps={{ shrink: true }} helperText="Si no se indica, el cambio es inmediato" />
              </>
            )}
            {plan?.code === 'free' && <Alert severity="warning">Pasa al plan Gratis: pierde la suscripción y los límites bajan a los del plan gratis.</Alert>}
            <TextField label="Motivo del cambio (opcional)" value={reason} onChange={e => setReason(e.target.value)} multiline minRows={2} />
          </Stack>
        </DialogContent>
        <Actions onClose={onClose} busy={busy} label="Cambiar plan" disabled={planId === ''}
          onSave={() => planId !== '' && onSave({ planId, months, effectiveDate: effective || undefined, reason: reason.trim() || undefined })} />
      </Dialog>
    );
  };

const BillingEmailDialog: React.FC<WithSave<string>> = ({ open, tenant, busy, onClose, onSave }) => {
  const [email, setEmail] = useState('');
  useEffect(() => { if (open) setEmail(tenant.billingEmail || ''); }, [open, tenant]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Email de MercadoPago</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ my: 1 }}>Se usa para la suscripción de MercadoPago. Vacío = el email del admin ({tenant.adminEmail || '—'}).</Alert>
        <TextField fullWidth type="email" label="Email de MercadoPago" value={email} onChange={e => setEmail(e.target.value)}
          helperText="Tiene que coincidir con la cuenta de MercadoPago del cliente" />
      </DialogContent>
      <Actions onClose={onClose} busy={busy} onSave={() => onSave(email.trim())} />
    </Dialog>
  );
};

const COUNTRIES: { code: string; name: string }[] = [
  { code: 'AR', name: 'Argentina' }, { code: 'UY', name: 'Uruguay' }, { code: 'CL', name: 'Chile' }, { code: 'PY', name: 'Paraguay' },
  { code: 'BO', name: 'Bolivia' }, { code: 'PE', name: 'Perú' }, { code: 'CO', name: 'Colombia' }, { code: 'MX', name: 'México' },
  { code: 'ES', name: 'España' }, { code: 'US', name: 'Estados Unidos' },
];

const BillingCountryDialog: React.FC<WithSave<{ country: string; preferredPaymentProvider: string }>> = ({ open, tenant, busy, onClose, onSave }) => {
  const [country, setCountry] = useState('AR');
  const [provider, setProvider] = useState('');
  useEffect(() => { if (open) { setCountry(tenant.country || 'AR'); setProvider(tenant.preferredPaymentProvider || ''); } }, [open, tenant]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>País y medio de cobro</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="País de facturación" value={country} onChange={e => setCountry(e.target.value)}>
            {COUNTRIES.map(c => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select label="Cobrar con" value={provider} onChange={e => setProvider(e.target.value)}
            helperText="Automático: MercadoPago en Argentina, Stripe en el resto">
            <MenuItem value="">Automático según el país</MenuItem>
            <MenuItem value="mercadopago">MercadoPago</MenuItem>
            <MenuItem value="stripe">Stripe</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <Actions onClose={onClose} busy={busy} onSave={() => onSave({ country, preferredPaymentProvider: provider })} />
    </Dialog>
  );
};

const ManualPaymentDialog: React.FC<WithSave<any> & { plans: SaPlan[]; onCopyReminder: () => Promise<string> }> =
  ({ open, tenant, plans, busy, onClose, onSave, onCopyReminder }) => {
    const [paymentDate, setPaymentDate] = useState(todayAr());
    const [periodStart, setPeriodStart] = useState('');
    const [override, setOverride] = useState(false);
    const [planId, setPlanId] = useState<number | ''>('');
    const [months, setMonths] = useState(1);
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('Transferencia');
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [waMessage, setWaMessage] = useState('');
    const paidPlans = plans.filter(p => p.code !== 'free' && (p.isActive || p.id === tenant.planId));
    const priceFor = (id: number | '', m: number) => {
      const p = paidPlans.find(x => x.id === (id === '' ? tenant.planId : id));
      return p ? String(p.price * m) : '';
    };
    useEffect(() => {
      if (!open) return;
      setPaymentDate(todayAr()); setPeriodStart(''); setOverride(false); setPlanId(''); setMonths(1);
      setAmount(priceFor('', 1)); setMethod('Transferencia'); setReference(''); setNotes(''); setWaMessage('');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tenant]);
    const current = paidPlans.find(p => p.id === tenant.planId);
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar pago manual · {tenant.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Fecha de pago" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Inicio del período (opcional)" value={periodStart} onChange={e => setPeriodStart(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Plan" value={planId}
                onChange={e => { const id = e.target.value === '' ? '' : Number(e.target.value); setPlanId(id); setAmount(priceFor(id, months)); }}>
                <MenuItem value=""><em>Mantener el actual{current ? ` (${current.name})` : ''}</em></MenuItem>
                {paidPlans.map(p => <MenuItem key={p.id} value={p.id}>{p.name} — {money(p.price, p.currency)}/mes</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Meses que paga" value={months}
                onChange={e => { const m = Number(e.target.value); setMonths(m); setAmount(priceFor(planId, m)); }}>
                {MONTH_OPTIONS.map(m => <MenuItem key={m} value={m}>{m} {m === 1 ? 'mes' : 'meses'}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Monto cobrado" value={amount} onChange={e => setAmount(e.target.value)} helperText="Podés cobrar distinto al precio de lista" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Método" value={method} onChange={e => setMethod(e.target.value)} placeholder="Transferencia / Efectivo / Tarjeta" />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Checkbox checked={override} onChange={e => setOverride(e.target.checked)} />}
                label="Usar la fecha de pago como inicio del período (si no, se suma al vencimiento vigente)" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Referencia (opcional)" value={reference} onChange={e => setReference(e.target.value)} placeholder="Comprobante, ID de transacción…" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Notas (opcional)" value={notes} onChange={e => setNotes(e.target.value)} />
            </Grid>
            {waMessage && <Grid item xs={12}><Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>{waMessage}</Alert></Grid>}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<WhatsAppIcon />} onClick={async () => setWaMessage(await onCopyReminder())}>Copiar recordatorio</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button variant="contained" color="success" disabled={busy} onClick={() => onSave({
            paymentDate: paymentDate || undefined,
            periodStartDate: periodStart || undefined,
            overrideFromPaymentDate: override,
            months,
            planId: planId === '' ? undefined : planId,
            amount: amount === '' ? undefined : Number(amount),
            method: method.trim() || undefined,
            reference: reference.trim() || undefined,
            notes: notes.trim() || undefined,
          })}>
            {busy ? <CircularProgress size={20} /> : 'Registrar pago'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

const AdminWhatsAppDialog: React.FC<Base & { onCopied: (t: string) => void }> = ({ open, tenant, onClose, onCopied }) => {
  const phone = tenant.adminPhone || '';
  const wa = waDigits(phone) ? `https://wa.me/${waDigits(phone)}` : '';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>WhatsApp del admin</DialogTitle>
      <DialogContent>
        {!phone ? <Alert severity="warning" sx={{ mt: 1 }}>El teléfono del admin no está cargado. Usá "Editar teléfono".</Alert> : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1}>
              <TextField fullWidth label="Teléfono" value={phone} InputProps={{ readOnly: true }} />
              <Button variant="outlined" onClick={async () => { await copy(phone); onCopied('Número copiado'); }}>Copiar</Button>
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField fullWidth label="Enlace de WhatsApp" value={wa} InputProps={{ readOnly: true }} />
              <Button variant="outlined" onClick={async () => { await copy(wa); onCopied('Enlace copiado'); }}>Copiar enlace</Button>
            </Stack>
            <Button variant="contained" color="success" startIcon={<WhatsAppIcon />} href={wa} target="_blank" rel="noopener">Abrir chat</Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
};

const EditPhoneDialog: React.FC<WithSave<string>> = ({ open, tenant, busy, onClose, onSave }) => {
  const [phone, setPhone] = useState('');
  useEffect(() => { if (open) setPhone(tenant.adminPhone || ''); }, [open, tenant]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Teléfono del admin</DialogTitle>
      <DialogContent>
        <TextField fullWidth sx={{ mt: 1 }} type="tel" label="Teléfono" value={phone} onChange={e => setPhone(e.target.value)}
          placeholder="Ej: 11 5555-4444" helperText="Se usa para WhatsApp (si no tiene código de país se asume Argentina)" />
      </DialogContent>
      <Actions onClose={onClose} busy={busy} onSave={() => onSave(phone.trim())} />
    </Dialog>
  );
};

const readTemplate = () => { try { return localStorage.getItem(TEMPLATE_KEY) || DEFAULT_TEMPLATE; } catch { return DEFAULT_TEMPLATE; } };

const AccessMessageDialog: React.FC<Base & { onEditTemplate: () => void; onCopied: () => void; onError: (e: any) => void }> =
  ({ open, tenant, onClose, onEditTemplate, onCopied, onError }) => {
    const [message, setMessage] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    useEffect(() => {
      if (!open) return;
      setLoading(true);
      superAdminApi.getAccessInfo(tenant.id)
        .then(info => {
          setPhone(info.adminPhone);
          setMessage(readTemplate()
            .replace(/{adminName}/g, info.adminName || 'Admin')
            .replace(/{tenantName}/g, info.tenantName)
            .replace(/{loginUrl}/g, info.loginUrl)
            .replace(/{adminEmail}/g, info.adminEmail || 'No disponible'));
        })
        .catch(e => { onError(e); onClose(); })
        .finally(() => setLoading(false));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tenant]);
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <span>Mensaje de acceso</span>
            <Tooltip title="Editar plantilla"><IconButton size="small" onClick={onEditTemplate}><SettingsIcon fontSize="small" /></IconButton></Tooltip>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box> : (
            <>
              {phone && <TextField fullWidth size="small" label="Teléfono" value={phone} InputProps={{ readOnly: true }} sx={{ mt: 1 }} />}
              <TextField fullWidth multiline minRows={9} value={message} onChange={e => setMessage(e.target.value)} sx={{ mt: 2 }}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }} />
              <Typography variant="caption" color="text.secondary">Podés editar el mensaje antes de copiarlo; los cambios no tocan la plantilla. No incluye la contraseña.</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {waDigits(phone) && (
            <Button startIcon={<WhatsAppIcon />} color="success" href={`https://wa.me/${waDigits(phone)}?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener">
              Enviar por WhatsApp
            </Button>
          )}
          <Button onClick={onClose}>Cerrar</Button>
          <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={async () => { await copy(message); onCopied(); }}>Copiar mensaje</Button>
        </DialogActions>
      </Dialog>
    );
  };

const TemplateDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [text, setText] = useState('');
  useEffect(() => { if (open) setText(readTemplate()); }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Plantilla del mensaje de acceso</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ my: 1 }}>
          Variables: <code>{'{adminName}'}</code>, <code>{'{tenantName}'}</code>, <code>{'{loginUrl}'}</code>, <code>{'{adminEmail}'}</code>. Se guarda en este navegador.
        </Alert>
        <TextField fullWidth multiline minRows={10} value={text} onChange={e => setText(e.target.value)} InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }} />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={() => setText(DEFAULT_TEMPLATE)}>Restaurar por defecto</Button>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={() => { try { localStorage.setItem(TEMPLATE_KEY, text); } catch { /* sin storage */ } onClose(); }}>Guardar plantilla</Button>
      </DialogActions>
    </Dialog>
  );
};

const ColorsDialog: React.FC<WithSave<{ primaryColor: string; secondaryColor: string }>> = ({ open, tenant, busy, onClose, onSave }) => {
  const [primary, setPrimary] = useState('#384DF4');
  const [secondary, setSecondary] = useState('#0A0A0A');
  useEffect(() => { if (open) { setPrimary(tenant.primaryColor || '#384DF4'); setSecondary(tenant.secondaryColor || '#0A0A0A'); } }, [open, tenant]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Colores · {tenant.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {[{ label: 'Color primario', v: primary, set: setPrimary }, { label: 'Color secundario', v: secondary, set: setSecondary }].map(c => (
            <Stack key={c.label} direction="row" spacing={1} alignItems="center">
              <input type="color" value={/^#[0-9a-f]{6}$/i.test(c.v) ? c.v : '#000000'} onChange={e => c.set(e.target.value)}
                style={{ width: 48, height: 40, border: 'none', background: 'none', padding: 0 }} aria-label={c.label} />
              <TextField fullWidth size="small" label={c.label} value={c.v} onChange={e => c.set(e.target.value)} />
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={busy} onClick={() => onSave({ primaryColor: '', secondaryColor: '' })}>Volver a los de DashFlow</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button variant="contained" disabled={busy} onClick={() => onSave({ primaryColor: primary, secondaryColor: secondary })}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

const FlagsDialog: React.FC<Base & { onSaved: () => void; onError: (e: any) => void }> = ({ open, tenant, onClose, onSaved, onError }) => {
  const [flags, setFlags] = useState<{ key: string; enabled: boolean }[] | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setFlags(null);
    superAdminApi.getFeatureFlags(tenant.id).then(r => setFlags((r.data as any[]) || [])).catch(e => { onError(e); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenant]);
  const save = async () => {
    if (!flags) return;
    setSaving(true);
    try { await superAdminApi.updateFeatureFlags(tenant.id, flags); onSaved(); }
    catch (e) { onError(e); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Feature flags · {tenant.name}</DialogTitle>
      <DialogContent dividers>
        {!flags ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box> : (
          <Grid container>
            {flags.map(f => (
              <Grid item xs={12} sm={6} key={f.key}>
                <FormControlLabel label={<Typography variant="body2">{f.key}</Typography>}
                  control={<Switch size="small" checked={f.enabled} onChange={(_, v) => setFlags(x => x!.map(y => (y.key === f.key ? { ...y, enabled: v } : y)))} />} />
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <Actions onClose={onClose} busy={saving} onSave={save} disabled={!flags} />
    </Dialog>
  );
};

const ResetPasswordDialog: React.FC<WithSave<string>> = ({ open, tenant, busy, onClose, onSave }) => {
  const [email, setEmail] = useState('');
  useEffect(() => { if (open) setEmail(tenant.adminUserEmail || tenant.adminEmail || ''); }, [open, tenant]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mandar mail para restablecer contraseña</DialogTitle>
      <DialogContent>
        <TextField fullWidth sx={{ mt: 1 }} type="email" label="Email del usuario" value={email} onChange={e => setEmail(e.target.value)}
          helperText="Le llega un mail con un enlace para elegir una contraseña nueva" />
      </DialogContent>
      <Actions onClose={onClose} busy={busy} label="Mandar mail" disabled={!email.trim()} onSave={() => onSave(email.trim())} />
    </Dialog>
  );
};

const SetPasswordDialog: React.FC<WithSave<{ email: string; newPassword: string; userType: 'Admin' | 'SuperAdmin'; tenantId?: number }>> =
  ({ open, tenant, busy, onClose, onSave }) => {
    const [userType, setUserType] = useState<'Admin' | 'SuperAdmin'>('Admin');
    const [email, setEmail] = useState('');
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [error, setError] = useState('');
    useEffect(() => { if (open) { setUserType('Admin'); setEmail(tenant.adminUserEmail || tenant.adminEmail || ''); setPw(''); setPw2(''); setError(''); } }, [open, tenant]);
    const save = () => {
      if (!email.trim()) { setError('Ingresá el email del usuario'); return; }
      if (pw.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
      if (pw !== pw2) { setError('Las contraseñas no coinciden'); return; }
      onSave({ email: email.trim(), newPassword: pw, userType, tenantId: userType === 'Admin' ? tenant.id : undefined });
    };
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Establecer contraseña (directo)</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ my: 1 }}>Cambia la contraseña en el acto, sin la actual. Pasásela al usuario por un medio privado.</Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField select fullWidth label="Tipo de usuario" value={userType} onChange={e => setUserType(e.target.value as any)}>
                <MenuItem value="Admin">Usuario de {tenant.name}</MenuItem>
                <MenuItem value="SuperAdmin">Super-admin</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField fullWidth type="email" label="Email" value={email} onChange={e => setEmail(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="password" label="Nueva contraseña" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="password" label="Confirmar contraseña" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" />
            </Grid>
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <Actions onClose={onClose} busy={busy} onSave={save} />
      </Dialog>
    );
  };

const HistoryDialog: React.FC<Base> = ({ open, tenant, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Historial de acciones · {tenant.name}</DialogTitle>
    <DialogContent dividers>
      {tenant.superAdminNotes
        ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>{tenant.superAdminNotes}</Typography>
        : <Typography color="text.secondary">Todavía no hay acciones registradas.</Typography>}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
  </Dialog>
);

const ReasonDialog: React.FC<WithSave<string> & { title: string; text: string; color: 'warning' | 'error' }> = ({ open, tenant, busy, onClose, onSave, title, text, color }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title} · {tenant.name}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>{text}</Typography>
        <TextField fullWidth required multiline minRows={3} label="Motivo" value={reason} onChange={e => setReason(e.target.value)} autoFocus />
      </DialogContent>
      <Actions onClose={onClose} busy={busy} color={color} label={`Confirmar`} disabled={!reason.trim()} onSave={() => onSave(reason.trim())} />
    </Dialog>
  );
};

const ExtendDemoDialog: React.FC<WithSave<number>> = ({ open, tenant, busy, onClose, onSave }) => {
  const [days, setDays] = useState('7');
  useEffect(() => { if (open) setDays('7'); }, [open]);
  const n = parseInt(days, 10);
  const valid = n >= 1 && n <= 365;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Extender demo · {tenant.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Si la demo sigue vigente, los días se suman al vencimiento. Si ya venció (o pasó a Gratis), se cuentan desde hoy y la cuenta vuelve a Demo con todas las funciones.
        </Typography>
        <TextField fullWidth autoFocus type="number" label="Días a extender" value={days} onChange={e => setDays(e.target.value)}
          inputProps={{ min: 1, max: 365 }} error={!valid} helperText={!valid ? 'Entre 1 y 365' : undefined} />
      </DialogContent>
      <Actions onClose={onClose} busy={busy} label="Extender" disabled={!valid} onSave={() => onSave(n)} />
    </Dialog>
  );
};

const DeleteDialog: React.FC<WithSave<void>> = ({ open, tenant, busy, onClose, onSave }) => {
  const [confirm, setConfirm] = useState('');
  useEffect(() => { if (open) setConfirm(''); }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Eliminar tenant</DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}><b>¡Atención! Esta acción es irreversible.</b></Alert>
        <Typography variant="body2" component="div">
          Se borra <b>{tenant.name}</b> con todos sus datos:
          <ul>
            <li>Productos, stock, lotes y producción</li>
            <li>Clientes, ventas, cobros y facturas</li>
            <li>Compras, proveedores y gastos</li>
            <li>Usuarios ({tenant.usersCount}) y la suscripción</li>
          </ul>
        </Typography>
        <TextField fullWidth label={`Escribí "${tenant.subdomain}" para confirmar`} value={confirm} onChange={e => setConfirm(e.target.value)} />
      </DialogContent>
      <Actions onClose={onClose} busy={busy} color="error" label="Eliminar definitivamente" disabled={confirm.trim() !== tenant.subdomain} onSave={() => onSave()} />
    </Dialog>
  );
};

export default SATenants;
